/* eslint-disable no-multi-spaces */

/**
 * Storage interface for calls history. Made and received calls
 * are stored and recovered via this class.
 *
 * For now it uses localStorage but this design will let easily
 * switch to any other storage.
 */
import LocalStorage from 'Store/class/local_storage'

const LIMIT = 1000 // storage limit
const direct = {
  OUTBOUND: 'out',
  INBOUND: 'in',
  MISSED: 'mis'
}

const STORE = Symbol('private method to store call')

class CallsLog {
  /**
   * @public Store dialed call number.
   */
  storeDialed (userId, num) {
    return this[STORE](userId, num, null, direct.OUTBOUND)
  }

  /**
   * @public Store received call number.
   */
  storeReceived (userId, num, name) {
    return this[STORE](userId, num, name, direct.INBOUND)
  }

  /**
   * @public Store missed calls.
   */
  storeMissed (userId, num, name) {
    const call = this[STORE](userId, num, name, direct.MISSED)
    if (!call) return null
    LocalStorage.addCallMissed(userId)
    return call
  }

  /**
   * @static Clear missed calls
   */
  static clearMissed (userId) {
    LocalStorage.resetCallsMissed(userId)
  }

  /**
   * @static Get missed calls number
   */
  static getMissedCalls (userId) {
    return LocalStorage.callsMissed(userId)
  }

  /**
   * @static Helper function to get storage limit constant value
   */
  static limit () {
    return LIMIT
  }

  /**
   * @public Returns filtered list of calls.
   * If number is empty string or null will return all list.
   */
  filter (userId, num) {
    const list = this.list(userId)
    if (list.length === 0) return list

    if (!num) return list

    return list.filter(c => c.num.match(num))
  }

  /**
   * @static Returns list of all calls unsorted.
   */
  static list (userId) {
    const h = LocalStorage.callsHistory(userId)
    if (h === null) return []
    try {
      return JSON.parse(h)
    } catch (err) {
      return []
    }
  }

  /**
   * @static Returns legth of calls history list.
   */
  static len (userId) {
    return CallsLog.list(userId).length
  }

  /**
   * @static Returns true if call is outbound.
   */
  static isDialed (call) {
    return call.dir === direct.OUTBOUND
  }

  /**
   * @static Returns true if call is inbound.
   */
  static isRecevd (call) {
    return call.dir === direct.INBOUND
  }

  /**
   * @static public Returns true if call is missed.
   */
  static isMissed (call) {
    return call.dir === direct.MISSED
  }

  /**
   * @private Store dialed or received number in history.
   */
  [STORE] (userId, num, name, dir) {
    if (!num) return null
    const list = CallsLog.list(userId)
    name = name || ''

    const call = { time: Date.now(), num: num.toString(), name: name, dir: dir }
    list.unshift(call) // push to the top of the list
    if (list.length > LIMIT) {
      list.pop()
    }
    LocalStorage.setHistory(userId, JSON.stringify(list))
    return call
  }
}

export default CallsLog
